import React from "react";
import { FAQ_TOKENS } from "./collections";
import { fieldDomId, getIn, SCHEDULE_KINDS, describeSchedule } from "./formModel";
import { isPastBusinessDate } from "../../../services/utils/dateUtils";
import { FileField, ImageField } from "./MediaFields";

/**
 * Inputs for a content item, generated from the field specs in
 * collections.js. One column, labels above inputs, hints and errors tied to
 * the input with aria-describedby, and a character count once the text is
 * near its limit.
 *
 * Every input's id is fieldDomId(path), so the editor can focus the input a
 * client-side check or the server's `error.field` points at.
 */

/** Shown from 80% of the limit, so short fields stay quiet. */
const COUNTER_FROM = 0.8;

const describedBy = (...ids) => ids.filter(Boolean).join(" ") || undefined;

function Hint({ id, children }) {
  return children ? <p id={id} className="cms-hint">{children}</p> : null;
}

function ErrorText({ id, message }) {
  return message ? <p id={id} className="cms-field-error">{message}</p> : null;
}

function Counter({ id, value, max }) {
  const length = String(value || "").trim().length;
  if (!max || length < max * COUNTER_FROM) return null;
  return (
    <p id={id} className={`cms-count${length > max ? " is-over" : ""}`}>
      {length} of {max} characters
    </p>
  );
}

/** text, textarea, markdown and date inputs. */
function TextField({ spec, path, value, error, onChange, label }) {
  const id = fieldDomId(path);
  const multiline = spec.kind === "textarea" || spec.kind === "markdown";
  const hintId = spec.hint ? `${id}-hint` : null;
  const errorId = error ? `${id}-error` : null;
  const countId = `${id}-count`;
  const common = {
    id,
    name: path,
    value: value || "",
    onChange: (e) => onChange(path, e.target.value),
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy(hintId, spec.max ? countId : null, errorId),
    className: "cms-input",
  };
  return (
    <div className="cms-field">
      <label htmlFor={id} className="cms-label">
        {label || spec.label}
        {!spec.required && spec.kind !== "date" && <span className="cms-optional"> (optional)</span>}
      </label>
      <Hint id={hintId}>{spec.hint}</Hint>
      {multiline ? (
        <textarea {...common} rows={spec.rows || 4} />
      ) : (
        <input
          {...common}
          type={spec.kind === "date" ? "date" : "text"}
          inputMode={spec.inputMode}
          autoCapitalize={spec.inputMode === "url" ? "none" : undefined}
          autoCorrect={spec.inputMode === "url" ? "off" : undefined}
        />
      )}
      {spec.kind === "markdown" && <FormattingHint />}
      <Counter id={countId} value={value} max={spec.max} />
      <ErrorText id={errorId} message={error} />
    </div>
  );
}

/** What an FAQ answer may contain (§3.13.3), in plain words. */
export function FormattingHint() {
  return (
    <details className="cms-help">
      <summary>Formatting and placeholders you can use</summary>
      <ul>
        <li>Leave a blank line to start a new paragraph.</li>
        <li><code>**bold words**</code> for bold and <code>*slanted words*</code> for italics.</li>
        <li>Start a line with <code>- </code> for a bullet point.</li>
        <li>
          Links: <code>[words to show](https://address)</code>. The address can also be{" "}
          <code>mailto:you@example.com</code> or a page here like <code>/policies</code>.
        </li>
        <li>
          Placeholders fill in values that can change, so the answer is never out of date:{" "}
          {FAQ_TOKENS.map((t, i) => (
            <React.Fragment key={t}>
              {i > 0 && ", "}
              <code>{`{{${t}}}`}</code>
            </React.Fragment>
          ))}
          , and <code>{"{{price:service-name}}"}</code> for a session price.
        </li>
      </ul>
    </details>
  );
}

function CheckboxField({ spec, path, value, onChange }) {
  const id = fieldDomId(path);
  return (
    <div className="cms-field">
      <label className="cms-check" htmlFor={id}>
        <input id={id} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(path, e.target.checked)} />
        <span>{spec.label}</span>
      </label>
    </div>
  );
}

function ChoiceField({ spec, path, value, error, onChange, options }) {
  const id = fieldDomId(path);
  return (
    <fieldset className="cms-fieldset cms-choice" id={id} tabIndex={-1} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="cms-label">{spec.label}</legend>
      {(options || spec.options).map((option) => (
        <label key={option.value} className="cms-radio" htmlFor={`${id}-${option.value}`}>
          <input
            id={`${id}-${option.value}`}
            type="radio"
            name={path}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(path, option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
      <ErrorText id={error ? `${id}-error` : null} message={error} />
    </fieldset>
  );
}

/** A repeatable list of strings: price lines, paragraphs, phone numbers. */
function ListField({ spec, path, value, errors, onChange }) {
  const id = fieldDomId(path);
  const items = value || [];
  const listError = errors[path];
  const full = spec.maxItems && items.length >= spec.maxItems;
  const removable = items.length > (spec.minItems || 0);
  return (
    <fieldset className="cms-fieldset" id={id} tabIndex={-1}>
      <legend className="cms-label">
        {spec.label}
        {!spec.minItems && <span className="cms-optional"> (optional)</span>}
      </legend>
      <Hint id={`${id}-hint`}>{spec.hint}</Hint>
      {items.map((item, i) => {
        const itemPath = `${path}.${i}`;
        const label = `${spec.itemLabel} ${i + 1}`;
        return (
          <div className="cms-list-item" key={i}>
            <TextField
              spec={{ ...spec.item, required: true }}
              path={itemPath}
              value={item}
              error={errors[itemPath]}
              onChange={onChange}
              label={label}
            />
            {removable && (
              <button
                type="button"
                className="admin-btn cms-btn-small"
                aria-label={`Remove ${label.toLowerCase()}`}
                onClick={() => onChange(path, items.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            )}
          </div>
        );
      })}
      {!full && (
        <button type="button" className="admin-btn" onClick={() => onChange(path, [...items, ""])}>
          + {spec.addLabel}
        </button>
      )}
      <ErrorText id={listError ? `${id}-error` : null} message={listError} />
    </fieldset>
  );
}

/** A repeatable list of objects: the places in a resources list. */
function EntriesField({ spec, path, value, errors, onChange, blankEntry }) {
  const id = fieldDomId(path);
  const items = value || [];
  const listError = errors[path];
  const full = spec.maxItems && items.length >= spec.maxItems;
  const removable = items.length > (spec.minItems || 0);
  return (
    <fieldset className="cms-fieldset" id={id} tabIndex={-1}>
      <legend className="cms-label">{spec.label}</legend>
      {items.map((entry, i) => {
        const entryPath = `${path}.${i}`;
        const name = (entry && entry[spec.fields[0].key]) || "";
        const label = `${spec.itemLabel} ${i + 1}`;
        return (
          <fieldset
            className="cms-entry"
            key={i}
            id={fieldDomId(entryPath)}
            tabIndex={-1}
            aria-describedby={errors[entryPath] ? `${fieldDomId(entryPath)}-error` : undefined}
          >
            <legend className="cms-entry__title">
              {label}
              {name ? `: ${name}` : ""}
            </legend>
            <ErrorText id={errors[entryPath] ? `${fieldDomId(entryPath)}-error` : null} message={errors[entryPath]} />
            <Fields fields={spec.fields} values={entry} errors={errors} onChange={onChange} prefix={`${entryPath}.`} />
            {removable && (
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                aria-label={`Remove ${label.toLowerCase()}${name ? ` (${name})` : ""}`}
                onClick={() => onChange(path, items.filter((_, j) => j !== i))}
              >
                Remove this place
              </button>
            )}
          </fieldset>
        );
      })}
      {!full && (
        <button type="button" className="admin-btn" onClick={() => onChange(path, [...items, blankEntry()])}>
          + {spec.addLabel}
        </button>
      )}
      <ErrorText id={listError ? `${id}-error` : null} message={listError} />
    </fieldset>
  );
}

/** A group's schedule: the kind in words, then the cycle's days, dates and time. */
function ScheduleField({ spec, path, value, errors, onChange }) {
  const id = fieldDomId(path);
  const s = value || {};
  const past = s.kind === "cycle" && s.end && /^\d{4}-\d{2}-\d{2}$/.test(s.end) && isPastBusinessDate(s.end);
  return (
    <fieldset className="cms-fieldset" id={id} tabIndex={-1}>
      <legend className="cms-label">{spec.label}</legend>
      <ChoiceField
        spec={{ label: "How the dates show" }}
        path={`${path}.kind`}
        value={s.kind}
        error={errors[`${path}.kind`]}
        onChange={onChange}
        options={SCHEDULE_KINDS}
      />
      {s.kind === "cycle" && (
        <div className="cms-schedule">
          <TextField spec={{ kind: "text", label: "Days", hint: "For example \"Thursdays\".", required: true, max: 40 }}
            path={`${path}.days`} value={s.days} error={errors[`${path}.days`]} onChange={onChange} />
          <TextField spec={{ kind: "date", label: "First day", required: true }}
            path={`${path}.start`} value={s.start} error={errors[`${path}.start`]} onChange={onChange} />
          <TextField spec={{ kind: "date", label: "Last day", required: true }}
            path={`${path}.end`} value={s.end} error={errors[`${path}.end`]} onChange={onChange} />
          <TextField spec={{ kind: "text", label: "Time", hint: "For example \"6:00 to 7:30 PM\".", required: true, max: 40 }}
            path={`${path}.time`} value={s.time} error={errors[`${path}.time`]} onChange={onChange} />
          {past && (
            <p className="admin-warning">
              These dates have passed, so the site shows the general note instead. Add the next cycle’s dates.
            </p>
          )}
        </div>
      )}
      {s.kind !== "cycle" && <p className="cms-hint">Shows as: {describeSchedule(s)}.</p>}
    </fieldset>
  );
}

/**
 * All fields of an item (or of one entry, with `prefix`).
 * @param {object} props
 * @param {object[]} props.fields - Field specs
 * @param {object} props.values - Form values for these fields
 * @param {object} props.errors - {path: message} for the whole form
 * @param {(path: string, value: any) => void} props.onChange - Path is absolute
 * @param {string} [props.prefix] - Path prefix, e.g. 'entries.2.'
 */
export default function Fields({ fields, values, errors, onChange, prefix = "" }) {
  return fields.map((spec) => {
    const path = `${prefix}${spec.key}`;
    const value = getIn(values, spec.key);
    const props = { spec, path, value, errors, error: errors[path], onChange };
    switch (spec.kind) {
      case "checkbox":
        return <CheckboxField key={path} {...props} />;
      case "choice":
        return <ChoiceField key={path} {...props} />;
      case "list":
        return <ListField key={path} {...props} />;
      case "entries":
        return (
          <EntriesField
            key={path}
            {...props}
            blankEntry={() => Object.fromEntries(spec.fields.map((f) => [f.key, f.kind === "list" ? [] : ""]))}
          />
        );
      case "schedule":
        return <ScheduleField key={path} {...props} />;
      case "image":
        return <ImageField key={path} {...props} />;
      case "file":
        return <FileField key={path} {...props} />;
      default:
        return <TextField key={path} {...props} />;
    }
  });
}
